import { type FC } from "react";

import { CloudFlowNodeType, ModelType } from "@doitintl/cmp-models";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from "@mui/material";

import { ReadonlyReferencedFieldChip } from "../../../ApiActionParametersForm/parameters/wrappers/ReferencedField/ReferencedFieldChip";

export const ListTagDocumentationModal: FC<{
  open: boolean;
  onClose: () => void;
}> = ({ open, onClose }) => {
  const mockReferenceableNodes = [
    {
      id: "mock-node-1",
      stepNumber: 2,
      name: "Get users",
      type: CloudFlowNodeType.ACTION,
      outputModel: {
        type: ModelType.STRUCTURE as const,
        members: {
          users: {
            model: {
              type: ModelType.STRUCTURE as const,
              members: {
                name: { model: { type: ModelType.STRING as const } },
                email: { model: { type: ModelType.STRING as const } },
              },
            },
          },
        },
      },
    },
  ];

  const mockReferencedValues = [
    {
      referencedNodeId: "mock-node-1",
      referencedField: ["users", "name"],
    },
    {
      referencedNodeId: "mock-node-1",
      referencedField: ["users", "email"],
    },
  ];

  const codeTagSx = { fontFamily: "monospace", color: "primary.main", fontWeight: 500 } as const;

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Using a list</DialogTitle>

      <DialogContent>
        <Stack gap={2}>
          <Typography variant="body1">
            When adding multiple references, by default they will present one after another. Use a list tag by adding{" "}
            <Typography component="span" sx={codeTagSx}>
              {"<list>"}
            </Typography>{" "}
            and{" "}
            <Typography component="span" sx={codeTagSx}>
              {"</list>"}
            </Typography>{" "}
            to get a more readable output.
          </Typography>

          <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
            Example 1 (without a list tag)
          </Typography>
          <Stack gap={0.5}>
            <Typography variant="body2" color="text.secondary">
              Input
            </Typography>
            <Box
              sx={(theme) => ({
                p: 1,
                backgroundColor: "general.backgroundDark",
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
              })}
            >
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                <ReadonlyReferencedFieldChip
                  referenceableNodes={mockReferenceableNodes}
                  value={mockReferencedValues[0]}
                  onDelete={() => {}}
                />
                <ReadonlyReferencedFieldChip
                  referenceableNodes={mockReferenceableNodes}
                  value={mockReferencedValues[1]}
                  onDelete={() => {}}
                />
              </Box>
            </Box>
          </Stack>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Output
            </Typography>
            <Box
              sx={(theme) => ({
                p: 1,
                backgroundColor: "general.backgroundDark",
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
              })}
            >
              <Typography sx={{ fontWeight: 400 }}>Alex Barnes, Joe Murphy, alex@email.com, joe@email.com</Typography>
            </Box>
          </Box>

          <Typography variant="subtitle1">Example 2 (with a list tag)</Typography>

          <Stack gap={0.5}>
            <Typography variant="body2" color="text.secondary">
              Input
            </Typography>
            <Box
              sx={(theme) => ({
                p: 1,
                backgroundColor: "general.backgroundDark",
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
              })}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                {"<list>"}
                <ReadonlyReferencedFieldChip
                  referenceableNodes={mockReferenceableNodes}
                  value={mockReferencedValues[0]}
                  onDelete={() => {}}
                />
                <Typography>-</Typography>
                <ReadonlyReferencedFieldChip
                  referenceableNodes={mockReferenceableNodes}
                  value={mockReferencedValues[1]}
                  onDelete={() => {}}
                />

                {"</list>"}
              </Box>
            </Box>
          </Stack>

          <Stack gap={0.5}>
            <Typography variant="body2" color="text.secondary">
              Output
            </Typography>
            <Box
              sx={(theme) => ({
                p: 1,
                backgroundColor: "general.backgroundDark",
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
              })}
            >
              <Box component="ul" sx={{ m: 0, pl: 2 }}>
                <li style={{ fontWeight: 400 }}>Alex Barnes - alex@email.com</li>
                <li style={{ fontWeight: 400 }}>Joe Murphy - joe@email.com</li>
              </Box>
            </Box>
          </Stack>
        </Stack>
      </DialogContent>
      <Divider />
      <DialogActions>
        <Button variant="contained" color="primary" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
